import { inject, Injectable } from '@angular/core';

import { Apollo, QueryRef } from 'apollo-angular';

import { filter, map, switchMap } from 'rxjs/operators';

import { zip } from 'rxjs';

import {
  PropertySearcherDocumentTypes,
  SelfDisclosureModel,
  SelfDisclosureQuestion,
  SelfDisclosureQuestionType
} from '@ui/shared/model';

import { FileUploadService, SelfDisclosureService } from '@ui/legacy-lib';

import {
  saveSelfDisclosureResponsesMutation,
  SelfDisclosureAnswerResponse,
  selfDisclosureQuery,
  SelfDisclosureQueryResponse,
  selfDisclosureResponses,
  SelfDisclosureSaveResponse
} from '../queries/self-disclosure.queries';

@Injectable({
  providedIn: 'root'
})
export class SelfDisclosureFacade {
  private apollo = inject(Apollo);
  private fileUploadService = inject(FileUploadService);
  private selfDisclosureService = inject(SelfDisclosureService);

  private selfDisclosureQuery: QueryRef<SelfDisclosureQueryResponse>;
  private selfDisclosureAnswers: QueryRef<SelfDisclosureAnswerResponse>;

  public loadSelfDisclosureModel(id: string) {
    this.selfDisclosureQuery =
      this.apollo.watchQuery<SelfDisclosureQueryResponse>({
        query: selfDisclosureQuery,
        variables: { id },
        fetchPolicy: 'no-cache'
      });

    return this.selfDisclosureQuery.valueChanges.pipe(
      filter(response => response.dataState === 'complete'),
      map(response => response.data)
    );
  }

  public saveAnswers(id: string, payload: SelfDisclosureModel) {
    const clonedPayload = structuredClone(payload);

    // Only send documents the landlord explicitly requested:
    // - top-level uploadedDocuments only when the template has a `documents` list
    // - DOCUMENT/DOCUMENTS (sub)questions only when the landlord enabled them
    //   (showSelfDisclosureQuestions === true)
    if (!clonedPayload.documents?.length) {
      clonedPayload.uploadedDocuments = [];
    }
    clonedPayload.questions = this.stripDocumentQuestions(
      clonedPayload.questions
    );

    const documents = clonedPayload.uploadedDocuments || [];

    const documentsToUpload = documents.filter(
      attachment => attachment.file instanceof Blob
    );
    const nestedDocumentsToUpload =
      this.selfDisclosureService.getDocumentsForUpload(clonedPayload.questions);

    return zip(
      this.fileUploadService.uploadDocuments(
        documentsToUpload,
        PropertySearcherDocumentTypes.SHARED_DOCUMENT
      ),
      this.fileUploadService.uploadDocuments(
        nestedDocumentsToUpload.map(element => element.file),
        PropertySearcherDocumentTypes.SHARED_DOCUMENT
      )
    ).pipe(
      map(([uploadedDocuments, nestedUploadedDocuments]) => {
        const documentsToSave = [...documents, ...uploadedDocuments].filter(
          attachment => attachment && !(attachment.file instanceof Blob)
        );

        // replace file-objects with attachment received from filer (upload call)
        nestedDocumentsToUpload.forEach((doc, index) => {
          const parent = doc.parentObject;
          const placeholderFile = doc.file; // placeholder file reference
          const uploadedFile = nestedUploadedDocuments[index]; // full uploaded file response

          if (!Array.isArray(parent.uploads)) {
            parent.uploads = [];
          }

          // Find index of the placeholder file by object reference
          const placeholderIndex = parent.uploads.indexOf(placeholderFile);

          if (placeholderIndex !== -1) {
            // Replace placeholder with uploaded file
            parent.uploads[placeholderIndex] = uploadedFile;
          } else {
            // If placeholder was not found, add uploaded file
            parent.uploads.push(uploadedFile);
          }
        });

        return {
          ...clonedPayload,
          uploadedDocuments: [...documentsToSave]
        };
      }),
      switchMap(input =>
        this.apollo.mutate<SelfDisclosureSaveResponse>({
          mutation: saveSelfDisclosureResponsesMutation,
          variables: { id, input }
        })
      )
    );
  }

  public getAnswers(id: string) {
    this.selfDisclosureAnswers =
      this.apollo.watchQuery<SelfDisclosureAnswerResponse>({
        query: selfDisclosureResponses,
        variables: { id },
        fetchPolicy: 'no-cache'
      });

    return this.selfDisclosureAnswers.valueChanges.pipe(
      filter(response => response.dataState === 'complete'),
      map(
        response =>
          response?.data?.getSelfDisclosureResponses?.data?.answers || {}
      )
    );
  }

  public refetch(): void {
    void this.selfDisclosureAnswers.refetch();
  }

  private stripDocumentQuestions(
    questions: SelfDisclosureQuestion[]
  ): SelfDisclosureQuestion[] {
    if (!Array.isArray(questions)) return questions;
    return questions
      .filter(q => !this.isUnrequestedDocumentQuestion(q))
      .map(q => ({
        ...q,
        subQuestions: this.stripDocumentQuestions(q.subQuestions)
      }));
  }

  private isUnrequestedDocumentQuestion(q: SelfDisclosureQuestion): boolean {
    const isDocument =
      q.type === SelfDisclosureQuestionType.DOCUMENT ||
      q.type === SelfDisclosureQuestionType.DOCUMENTS;
    return isDocument && q.showSelfDisclosureQuestions !== true;
  }
}
